import { appLink, summaryText } from './share';

/*
 * One field collection (a finding logged in the system) as something to
 * share or keep (2026-10-19): a link that opens it for whoever may read it
 * (/collections?open=<id>, or Field Mode's own page for its collector), a
 * plain-text summary, and Markdown and JSON exports.
 *
 * Built from what this reader was sent (GET /collections/{id} and its
 * identifications and vegetation): the server already left out whatever they
 * may not see -- a collector reading another's record gets no coordinates --
 * so nothing here can share more than the page shows.
 */
const FIELDS = [
  ['code', (c) => c.code || c.qr_uid],
  ['taxon', (c) => [c.current_taxon_name, c.current_rank].filter(Boolean).join(' · ')],
  ['collected', (c) => (c.collection_datetime ? String(c.collection_datetime).slice(0, 16).replace('T', ' ') : null)],
  ['collector', (c) => c.observer || c.original_collector || c.collector_name],
  ['place', (c) => [c.locality_text, c.region].filter(Boolean).join(' · ')],
  ['coordinates', (c) => (c.gps_lat != null && c.gps_lng != null ? `${c.gps_lat}, ${c.gps_lng}` : null)],
  ['altitude', (c) => (c.altitude_m != null ? `${c.altitude_m} m` : null)],
  ['substrate', (c) => c.substrate],
  ['host', (c) => c.host_species],
  ['habitat', (c) => [c.habitat_biome, c.microenvironment].filter(Boolean).join(' · ')],
  ['count', (c) => c.fruiting_body_count],
  ['stage', (c) => c.phenological_stage],
  ['labStatus', (c) => c.lab_status],
  ['isolate', (c) => c.specimen_code],
  ['notes', (c) => c.general_notes],
];

export function collectionLink(collection, { field = false } = {}) {
  const id = collection?.sample_id;
  if (!id) return null;
  return appLink(field ? `/field/collection/${id}` : `/collections?open=${encodeURIComponent(id)}`);
}

function rows(collection, label) {
  return FIELDS.map(([key, get]) => [label(key), get(collection)]);
}

export function collectionTitle(collection, label) {
  return `${label('title')}: ${collection.code || collection.qr_uid || collection.sample_id}`
    + (collection.current_taxon_name ? ` (${collection.current_taxon_name})` : '');
}

/** The summary to paste into a message. */
export function collectionText(collection, label, url) {
  return summaryText(collectionTitle(collection, label), rows(collection, label), url);
}

const mdEscape = (value) => String(value).replace(/\|/g, '\\|').replace(/\n/g, ' ');

/** The record, its identifications and its vegetation, as Markdown. */
export function collectionMarkdown(collection, { identifications = [], vegetation = [] } = {}, label, url) {
  const out = [`# ${collectionTitle(collection, label)}`, ''];
  rows(collection, label).filter(([, v]) => v !== null && v !== undefined && v !== '')
    .forEach(([k, v]) => out.push(`- **${k}:** ${mdEscape(v)}`));
  if (identifications.length) {
    out.push('', `## ${label('identifications')}`, '', `| ${label('identDate')} | ${label('taxon')} | ${label('identMethod')} | ${label('identBy')} |`,
      '|---|---|---|---|');
    identifications.forEach((i) => out.push(`| ${mdEscape(String(i.identification_date || '').slice(0, 10))} | `
      + `${mdEscape(i.taxon_name || '')} | ${mdEscape(i.identification_method || '')} | `
      + `${mdEscape(i.identified_by_name || i.identified_by_username || '')} |`));
  }
  if (vegetation.length) {
    out.push('', `## ${label('vegetation')}`, '');
    vegetation.forEach((v) => out.push(`- ${mdEscape(v.species)}${v.is_dominant ? ` (${label('dominant')})` : ''}`));
  }
  if (url) out.push('', url);
  return `${out.join('\n')}\n`;
}

/** The same as JSON: the record as this reader has it, and its link. */
export function collectionJson(collection, { identifications = [], vegetation = [] } = {}, url) {
  return `${JSON.stringify({ collection, identifications, vegetation, link: url || null }, null, 2)}\n`;
}
